import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, Save, Loader2, User, GraduationCap, MapPin, Heart } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import PhotoUpload from '../components/PhotoUpload';

const RELIGIONS = ['Hindu', 'Muslim', 'Christian', 'Sikh', 'Buddhist', 'Jain', 'Other'];
const DIETS = ['Vegetarian', 'Non-Vegetarian', 'Eggetarian', 'Vegan', 'Jain'];
const INCOMES = ['Below 3 Lakh', '3-5 Lakh', '5-8 Lakh', '8-12 Lakh', '12-20 Lakh', '20-30 Lakh', '30-50 Lakh', '50 Lakh+'];
const COMPLEXIONS = ['Very Fair', 'Fair', 'Wheatish', 'Dark'];
const FAMILY_TYPES = ['Nuclear', 'Joint', 'Extended'];
const DEGREES = ['High School', 'Diploma', 'Bachelors', 'Masters', 'PhD', 'Professional (CA/CS/MBBS/LLB)'];

export default function ProfileEditPage() {
  const { t } = useLanguage();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({ ...user });
    }
  }, [user]);

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const { id, firebase_uid, phone, created_at, kyc_status, subscription, verified_badge, profile_views, last_seen, ...editable } = form;
      await api.put('/profiles/me', editable);
      await refreshUser();
      toast.success('Profile updated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const renderField = (label, field, type = 'text', placeholder = '') => (
    <div>
      <Label className="text-sm font-medium text-[#2D2424]">{label}</Label>
      <Input type={type} value={form[field] || ''} onChange={(e) => update(field, e.target.value)}
        placeholder={placeholder} className="mt-1.5 h-11 rounded-lg border-[#E7E5E4] focus:border-[#D9381E]"
        data-testid={`edit-${field}`} />
    </div>
  );

  const renderSelect = (label, field, options) => (
    <div>
      <Label className="text-sm font-medium text-[#2D2424]">{label}</Label>
      <Select value={form[field] || ''} onValueChange={(v) => update(field, v)}>
        <SelectTrigger className="mt-1.5 h-11 rounded-lg border-[#E7E5E4]" data-testid={`edit-${field}`}>
          <SelectValue placeholder={`Select...`} />
        </SelectTrigger>
        <SelectContent>
          {options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  if (!user) return null;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="profile-edit-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="rounded-full">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <h1 className="text-2xl font-bold text-[#2D2424]">{t('profile.edit')}</h1>
        </div>
        <Button onClick={handleSave} disabled={saving}
          className="bg-[#D9381E] hover:bg-[#B91C1C] text-white rounded-full px-6" data-testid="save-profile-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          {t('common.save')}
        </Button>
      </div>

      <Tabs defaultValue="basic" className="space-y-6">
        <TabsList className="bg-[#F9F5F0] rounded-full p-1 flex-wrap">
          <TabsTrigger value="basic" className="rounded-full data-[state=active]:bg-[#D9381E] data-[state=active]:text-white">
            <User className="w-3.5 h-3.5 mr-1" /> {t('register.step1')}
          </TabsTrigger>
          <TabsTrigger value="education" className="rounded-full data-[state=active]:bg-[#D9381E] data-[state=active]:text-white">
            <GraduationCap className="w-3.5 h-3.5 mr-1" /> {t('register.step2')}
          </TabsTrigger>
          <TabsTrigger value="lifestyle" className="rounded-full data-[state=active]:bg-[#D9381E] data-[state=active]:text-white">
            <MapPin className="w-3.5 h-3.5 mr-1" /> {t('register.step3')}
          </TabsTrigger>
          <TabsTrigger value="preferences" className="rounded-full data-[state=active]:bg-[#D9381E] data-[state=active]:text-white">
            <Heart className="w-3.5 h-3.5 mr-1" /> {t('register.step4')}
          </TabsTrigger>
          <TabsTrigger value="photos" className="rounded-full data-[state=active]:bg-[#D9381E] data-[state=active]:text-white">
            {t('profile.photos')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="basic">
          <Card className="p-6 rounded-2xl border border-[#E7E5E4] bg-white space-y-4">
            {renderField(t('register.name'), 'name')}
            <div>
              <Label className="text-sm font-medium">{t('register.gender')}</Label>
              <RadioGroup value={form.gender || ''} onValueChange={(v) => update('gender', v)} className="mt-2 flex gap-4">
                {['male', 'female'].map(g => (
                  <Label key={g} className={`px-6 py-2.5 rounded-full border cursor-pointer text-sm ${form.gender === g ? 'border-[#D9381E] bg-[#D9381E]/5 text-[#D9381E]' : 'border-[#E7E5E4]'}`}>
                    <RadioGroupItem value={g} className="sr-only" />{t(`register.${g}`)}
                  </Label>
                ))}
              </RadioGroup>
            </div>
            {renderField(t('register.dob'), 'dob', 'date')}
            <div className="grid grid-cols-2 gap-3">
              {renderField('Birth Time', 'birth_time', 'time')}
              {renderField('Birth Place', 'birth_place', 'text')}
            </div>
            {renderSelect(t('register.religion'), 'religion', RELIGIONS)}
            <div className="grid grid-cols-2 gap-3">
              {renderField(t('register.caste'), 'caste')}
              {renderField(t('register.sub_caste'), 'sub_caste')}
            </div>
            <div className="grid grid-cols-3 gap-3">
              {renderField(t('register.height'), 'height', 'number')}
              {renderField(t('register.weight'), 'weight', 'number')}
              {renderSelect(t('register.complexion'), 'complexion', COMPLEXIONS)}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="education">
          <Card className="p-6 rounded-2xl border border-[#E7E5E4] bg-white space-y-4">
            {renderSelect(t('register.education_degree'), 'education_degree', DEGREES)}
            {renderField(t('register.education_field'), 'education_field')}
            {renderField(t('register.education_college'), 'education_college')}
            {renderField(t('register.occupation'), 'occupation')}
            {renderField(t('register.company'), 'company')}
            {renderSelect(t('register.income'), 'income', INCOMES)}
            {renderSelect(t('register.family_type'), 'family_type', FAMILY_TYPES)}
            <div className="grid grid-cols-2 gap-3">
              {renderField(t('register.father_occupation'), 'father_occupation')}
              {renderField(t('register.mother_occupation'), 'mother_occupation')}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="lifestyle">
          <Card className="p-6 rounded-2xl border border-[#E7E5E4] bg-white space-y-4">
            {renderSelect(t('register.diet'), 'diet', DIETS)}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium">{t('register.smoking')}</Label>
                <RadioGroup value={form.smoking || 'no'} onValueChange={(v) => update('smoking', v)} className="mt-2 flex gap-3">
                  {['no', 'yes'].map(v => (
                    <Label key={v} className={`px-4 py-2 rounded-full border cursor-pointer text-sm ${form.smoking === v ? 'border-[#D9381E] bg-[#D9381E]/5 text-[#D9381E]' : 'border-[#E7E5E4]'}`}>
                      <RadioGroupItem value={v} className="sr-only" />{t(`common.${v}`)}
                    </Label>
                  ))}
                </RadioGroup>
              </div>
              <div>
                <Label className="text-sm font-medium">{t('register.drinking')}</Label>
                <RadioGroup value={form.drinking || 'no'} onValueChange={(v) => update('drinking', v)} className="mt-2 flex gap-3">
                  {['no', 'yes'].map(v => (
                    <Label key={v} className={`px-4 py-2 rounded-full border cursor-pointer text-sm ${form.drinking === v ? 'border-[#D9381E] bg-[#D9381E]/5 text-[#D9381E]' : 'border-[#E7E5E4]'}`}>
                      <RadioGroupItem value={v} className="sr-only" />{t(`common.${v}`)}
                    </Label>
                  ))}
                </RadioGroup>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {renderField(t('register.city'), 'city')}
              {renderField(t('register.state'), 'state')}
            </div>
            {renderField(t('register.native_place'), 'native_place')}
            {renderField(t('register.hobbies'), 'hobbies')}
            <div>
              <Label className="text-sm font-medium">{t('register.bio')}</Label>
              <Textarea value={form.bio || ''} onChange={(e) => update('bio', e.target.value.slice(0, 500))}
                placeholder={t('register.bio_placeholder')} rows={4} maxLength={500}
                className="mt-1.5 rounded-lg border-[#E7E5E4]" data-testid="edit-bio" />
              <p className="text-xs text-[#78716C] mt-1">{(form.bio || '').length}/500</p>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="preferences">
          <Card className="p-6 rounded-2xl border border-[#E7E5E4] bg-white space-y-4">
            <div className="grid grid-cols-2 gap-3">
              {renderField(t('register.pref_age') + ' (Min)', 'pref_age_min', 'number')}
              {renderField(t('register.pref_age') + ' (Max)', 'pref_age_max', 'number')}
            </div>
            {renderSelect(t('register.pref_education'), 'pref_education', ['Any', ...DEGREES])}
            {renderSelect(t('register.pref_religion'), 'pref_religion', ['Any', ...RELIGIONS])}
            {renderField(t('register.pref_caste'), 'pref_caste')}
            {renderField(t('register.pref_location'), 'pref_location')}
            {renderSelect(t('register.pref_diet'), 'pref_diet', ['Any', ...DIETS])}
          </Card>
        </TabsContent>

        <TabsContent value="photos">
          <Card className="p-6 rounded-2xl border border-[#E7E5E4] bg-white">
            <PhotoUpload photos={form.photos || []} onUpdate={(photos) => {
              update('photos', photos);
              refreshUser();
            }} />
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
